import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useImperativeHandle,
  startTransition,
} from 'react';
import moment from 'moment-timezone';
import { useQuery } from '@tanstack/react-query';
import {
  useCoordinatorJobConfig,
  useCoordinatorTemplates,
} from '@/api/ai-coordinator';
import { InterviewDetailsAPI } from '@/pages/KInterviewScheduler/SchedulerDetails/api/InterviewDetailsAPI';
import { useGetAtsOfficesQuery } from '@/store/query/offices';
import { useShowToast } from '@/hooks';
import { useDateFormat } from '@/hooks/useDateFormat';
import { SCHEDULE_AI_COPY } from '../../constants';
import {
  getScheduleTimingSelectOptions,
  hasValidCandidateTimezone,
  isCustomScheduleInFuture,
} from '../../scheduleWithAiTimezoneUtils';
import {
  getFirstAvailableLocation,
  buildDisplayPlan,
  buildCandidateCurrentTimeLine,
  computeScheduledAtISO,
  parseCoordinatorScheduledAtToCustomSchedule,
} from './scheduleWithAiForm.pure';

const COPY = SCHEDULE_AI_COPY;

type PayloadLocationOffice = {
  location?: string;
  ats_office_id?: number | null;
};

/** `resolvePayloadValues` omits `ats_office_id` when unset — use strict checks only (eslint eqeqeq). */
function onsitePayloadMissingOffice(payload: PayloadLocationOffice): boolean {
  if (payload.location !== 'onsite') return false;
  const id = payload.ats_office_id;
  return id === undefined || id === null;
}

function onsitePayloadHasOfficeWhenRequired(
  payload: PayloadLocationOffice
): boolean {
  if (payload.location !== 'onsite') return true;
  const id = payload.ats_office_id;
  return id !== undefined && id !== null;
}

/**
 * Encapsulates all form state, pre-fill effects, derived values, and submit logic for Schedule with AI form.
 * Used by ScheduleWithAiFormContent to keep the main component thin.
 */
export function useScheduleWithAiForm(
  {
    interviewPlan,
    isPlanLoading,
    contact,
    activity,
    planInterviewers = [],
    needsInterviewerInput = false,
    userTimezoneKey = 'America/Los_Angeles',
    onSubmit,
    isSubmitting = false,
    candidateCountLabel,
    onScheduleStateChange,
    existingSession,
    /**
     * True while GET session is in flight (reschedule / `initialSessionId`). Avoids plan pre-fill
     * overwriting session and auto-edit latch before pre-fill.
     */
    isCoordinatorSessionLoading = false,
    jobId,
  },
  ref
) {
  const toast = useShowToast();
  const { momentDateTimeFormat } = useDateFormat('account');

  // --- Form state ---
  const [timing, setTiming] = useState('immediately');
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [customDateModalOpen, setCustomDateModalOpen] = useState(false);
  const [customSchedule, setCustomSchedule] = useState(null);
  const [interviewKind, setInterviewKind] = useState('one_on_one');
  const [selectedInterviewers, setSelectedInterviewers] = useState([]);
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [location, setLocation] = useState('');
  const [officeId, setOfficeId] = useState<number | null>(null);
  const [interviewName, setInterviewName] = useState('');
  const [isInterviewDetailsEditing, setIsInterviewDetailsEditing] =
    useState(false);
  const [noteTakerEnabled, setNoteTakerEnabled] = useState(false);
  const [calendarVisibility, setCalendarVisibility] = useState('default');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const newTemplateRowRef = useRef(null);
  const prevTemplateCountRef = useRef(0);
  const additionalContextFormikRef = useRef(null);
  const officeAddressFieldRef = useRef<HTMLDivElement | null>(null);
  const initialSnapshotRef = useRef(null);
  const [hasUnsavedChanges, setHasUnsavedChangesState] = useState(false);
  const [minuteTick, setMinuteTick] = useState(0);
  const lastSourceKeyRef = useRef(null);

  // --- Queries ---
  const { data: availableLocations = {} } = useQuery({
    queryKey: ['scheduleInterviewLocations'],
    queryFn: () => InterviewDetailsAPI.getInterviewLocations(),
    staleTime: 0,
  });

  const { data: offices = [], isLoading: isOfficeLoading } =
    useGetAtsOfficesQuery({});

  const hasCandidateTimezone = hasValidCandidateTimezone(contact);
  const candidateTimezoneKey = hasCandidateTimezone
    ? String(contact.time_zone)
    : userTimezoneKey;
  const candidateCurrentTime = moment()
    .tz(candidateTimezoneKey)
    .format('h:mm A z');

  useEffect(() => {
    const id = setInterval(() => setMinuteTick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const scheduleTimingOptions = useMemo(() => {
    void minuteTick;
    return getScheduleTimingSelectOptions({
      hasCandidateTimezone,
      displayTimezoneKey: candidateTimezoneKey,
    });
  }, [hasCandidateTimezone, candidateTimezoneKey, minuteTick]);

  useEffect(() => {
    if (!hasCandidateTimezone && timing === 'custom') {
      const id = setTimeout(() => {
        setTiming('immediately');
        setCustomSchedule(null);
      }, 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [hasCandidateTimezone, timing]);

  useEffect(() => {
    if (timing !== 'today_evening') return undefined;
    const sixPm = moment()
      .tz(candidateTimezoneKey)
      .startOf('day')
      .add(18, 'hours');
    if (!sixPm.isAfter(moment())) {
      const id = setTimeout(() => setTiming('immediately'), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [timing, candidateTimezoneKey, minuteTick]);

  // --- Prefill effects (plan, coordinator session, existing interview) ---
  useEffect(() => {
    if (!interviewPlan || existingSession || isCoordinatorSessionLoading)
      return;
    const planLocation =
      interviewPlan.location === 'unspecified' || !interviewPlan.location
        ? ''
        : interviewPlan.location;
    const id = setTimeout(() => {
      setInterviewName(
        interviewPlan?.substituted_name ||
          interviewPlan?.name ||
          activity?.label ||
          ''
      );
      setInterviewKind(
        interviewPlan.kind || interviewPlan.interview_kind || 'one_on_one'
      );
      setDurationMinutes(interviewPlan.duration ?? 60);
      const resolvedLocation =
        planLocation || getFirstAvailableLocation(availableLocations);
      setLocation(resolvedLocation);
      setNoteTakerEnabled(!!interviewPlan.note_taker_enabled);
      setCalendarVisibility(
        interviewPlan.calendar_visibility === 'private' ? 'private' : 'default'
      );
      const planOfficeId =
        (interviewPlan as { ats_office_id?: number })?.ats_office_id ?? null;
      if (resolvedLocation === 'onsite' && planOfficeId !== null) {
        setOfficeId(planOfficeId);
      } else {
        setOfficeId(null);
      }
    }, 0);
    return () => clearTimeout(id);
  }, [
    interviewPlan,
    existingSession,
    activity?.label,
    availableLocations,
    isCoordinatorSessionLoading,
  ]);

  useEffect(() => {
    if (!existingSession) return;
    const config = existingSession.interview_config;
    const sessionTemplateIds = existingSession.coordinator_templates;
    const statusNorm = String(existingSession.status ?? '')
      .toLowerCase()
      .trim();
    let rawScheduledIso: string | null | undefined;
    if (statusNorm === 'callback_scheduled') {
      rawScheduledIso =
        existingSession.scheduled_callback_at ?? existingSession.scheduled_at;
    } else if (statusNorm === 'retry_scheduled') {
      rawScheduledIso =
        existingSession.retry_at ?? existingSession.scheduled_at;
    } else {
      rawScheduledIso = existingSession.scheduled_at;
    }

    const id = setTimeout(() => {
      if (rawScheduledIso) {
        const custom = parseCoordinatorScheduledAtToCustomSchedule(
          rawScheduledIso,
          candidateTimezoneKey
        );
        if (custom) {
          setTiming('custom');
          setCustomSchedule(custom);
        } else {
          setTiming('immediately');
          setCustomSchedule(null);
        }
      } else {
        setTiming('immediately');
        setCustomSchedule(null);
      }
      if (config) {
        setInterviewName(
          config.name ?? interviewPlan?.name ?? activity?.label ?? ''
        );
        setInterviewKind(config.kind || 'one_on_one');
        setDurationMinutes(config.duration_minutes ?? 60);
        const loc =
          config.location === 'unspecified' || !config.location
            ? ''
            : config.location;
        setLocation(loc);
        if (
          Array.isArray(config.participant_ids) &&
          config.participant_ids.length > 0
        ) {
          setSelectedInterviewers(
            config.participant_ids.map((pid) => ({
              value: pid,
              label:
                planInterviewers.find((p) => p.id === pid)?.name ?? `ID ${pid}`,
            }))
          );
        }
      }
      if (Array.isArray(sessionTemplateIds) && sessionTemplateIds.length > 0) {
        setSelectedTemplateIds(
          sessionTemplateIds.map((t) =>
            typeof t === 'object' && t?.id !== null ? t.id : t
          )
        );
      }
      if (
        config?.note_taker_enabled !== undefined &&
        config?.note_taker_enabled !== null
      ) {
        setNoteTakerEnabled(!!config.note_taker_enabled);
      }
      if (
        config?.calendar_visibility !== undefined &&
        config?.calendar_visibility !== null
      ) {
        setCalendarVisibility(
          config.calendar_visibility === 'private' ? 'private' : 'default'
        );
      }
      const configOfficeId = (config as { ats_office_id?: number })
        ?.ats_office_id;
      const resolvedLoc =
        config?.location === 'unspecified' || !config?.location
          ? ''
          : (config?.location ?? '');
      if (resolvedLoc === 'onsite' && configOfficeId !== null) {
        setOfficeId(configOfficeId);
      } else {
        setOfficeId(null);
      }
    }, 0);
    return () => clearTimeout(id);
  }, [
    existingSession,
    interviewPlan?.name,
    activity?.label,
    planInterviewers,
    userTimezoneKey,
    candidateTimezoneKey,
  ]);

  const existingInterview = activity?.data;
  const hasExistingInterview =
    existingInterview &&
    !existingSession &&
    (existingInterview.start_time ||
      existingInterview.location ||
      (Array.isArray(existingInterview.interviewers) &&
        existingInterview.interviewers.length > 0));

  useEffect(() => {
    if (!hasExistingInterview) return;
    const interview = existingInterview;
    const id = setTimeout(() => {
      setInterviewName(
        interview?.name ?? interviewPlan?.name ?? activity?.label ?? ''
      );
      if (interview.location) {
        const loc =
          interview.location === 'unspecified' || !interview.location
            ? ''
            : interview.location;
        setLocation(loc);
        const invOfficeId = (interview as { ats_office_id?: number })
          ?.ats_office_id;
        if (loc === 'onsite' && invOfficeId !== null) {
          setOfficeId(invOfficeId);
        } else {
          setOfficeId(null);
        }
      }
      if (
        Array.isArray(interview.interviewers) &&
        interview.interviewers.length > 0
      ) {
        setSelectedInterviewers(
          interview.interviewers.map((inv) => ({
            value: inv.id ?? inv,
            label:
              typeof inv === 'object' &&
              inv.name !== undefined &&
              inv.name !== null
                ? inv.name
                : (planInterviewers.find((p) => p.id === (inv.id ?? inv))
                    ?.name ?? `ID ${inv.id ?? inv}`),
          }))
        );
      }
      if (interview.start_time && interview.end_time) {
        const start = moment(interview.start_time);
        const end = moment(interview.end_time);
        const durationMins = Math.max(15, end.diff(start, 'minutes'));
        setDurationMinutes(durationMins);
      } else if (
        interview.duration_minutes !== undefined &&
        interview.duration_minutes !== null
      ) {
        setDurationMinutes(interview.duration_minutes);
      }
      if (interview.interview_kind || interview.kind) {
        const kind = interview.interview_kind || interview.kind || 'one_on_one';
        setInterviewKind(
          kind === 'panel' || kind === 'one_on_one' ? kind : 'one_on_one'
        );
      }
      const templateIds = Array.isArray(interview.coordinator_templates)
        ? interview.coordinator_templates.map((t) =>
            typeof t === 'object' && t?.id !== null ? t.id : t
          )
        : interview.coordinator_template_id !== null
          ? [interview.coordinator_template_id]
          : null;
      if (templateIds?.length) {
        setSelectedTemplateIds(templateIds);
      }
    }, 0);
    return () => clearTimeout(id);
  }, [
    hasExistingInterview,
    existingInterview,
    interviewPlan?.name,
    activity?.label,
    planInterviewers,
    userTimezoneKey,
    candidateTimezoneKey,
  ]);

  useEffect(() => {
    if (location !== 'onsite') {
      const id = setTimeout(() => setOfficeId(null), 0);
      return () => clearTimeout(id);
    }
  }, [location]);

  // --- Interview details: required-field flags & editable vs read-only ---
  /** Onsite selected but office not chosen (required in EditableDetails). */
  const needsOfficeInput = location === 'onsite' && officeId === null;

  /**
   * Auto-open editable interview details only when something critical is missing:
   * no interviewers on the plan and none selected yet, or onsite without an office.
   * Otherwise use read-only summary until the user clicks Edit.
   */
  const sessionInterviewConfig = existingSession?.interview_config as
    | { participant_ids?: unknown[] }
    | undefined;
  const sessionWillSupplyInterviewers =
    Array.isArray(sessionInterviewConfig?.participant_ids) &&
    sessionInterviewConfig.participant_ids.length > 0;
  const activityWillSupplyInterviewers =
    !!existingInterview &&
    !existingSession &&
    Array.isArray(existingInterview.interviewers) &&
    existingInterview.interviewers.length > 0;

  const planReady =
    !isPlanLoading && Boolean(interviewPlan) && !isCoordinatorSessionLoading;
  const interviewersEmptyOnForm =
    planReady &&
    selectedInterviewers.length === 0 &&
    planInterviewers.length === 0 &&
    !sessionWillSupplyInterviewers &&
    !activityWillSupplyInterviewers;
  const shouldOpenInterviewDetailsAsEditable =
    interviewersEmptyOnForm || (planReady && needsOfficeInput);

  const showInterviewDetailsAsEditable =
    shouldOpenInterviewDetailsAsEditable || isInterviewDetailsEditing;

  /**
   * Latch edit mode when we auto-open for missing interviewers or office. Otherwise
   * `shouldOpenInterviewDetailsAsEditable` becomes false as soon as those are filled
   * and the section would drop back to read-only without this.
   * `startTransition` avoids synchronous setState in the effect body (cascading render lint).
   */
  useEffect(() => {
    if (!shouldOpenInterviewDetailsAsEditable) return;
    startTransition(() => {
      setIsInterviewDetailsEditing(true);
    });
  }, [shouldOpenInterviewDetailsAsEditable]);

  /** When auto-opening edit, seed interviewers from plan like the section Edit button. */
  const prevShowInterviewDetailsEditableRef = useRef(false);
  useEffect(() => {
    const now = showInterviewDetailsAsEditable;
    const wasFalseNowTrue = !prevShowInterviewDetailsEditableRef.current && now;
    prevShowInterviewDetailsEditableRef.current = now;
    if (!wasFalseNowTrue) return;
    if (planInterviewers.length === 0 || selectedInterviewers.length > 0)
      return;
    const id = setTimeout(() => {
      setSelectedInterviewers(
        planInterviewers.map((p) => ({
          ...p,
          value: p.id,
          label: p.name,
        }))
      );
    }, 0);
    return () => clearTimeout(id);
  }, [
    showInterviewDetailsAsEditable,
    planInterviewers,
    selectedInterviewers.length,
  ]);

  // --- Derived display (read-only summary, timing copy, scheduled-at ISO) ---
  const displayPlan = useMemo(
    () =>
      buildDisplayPlan({
        existingSession,
        existingInterview,
        interviewPlan: interviewPlan as
          | Record<string, unknown>
          | null
          | undefined,
        planInterviewers,
        offices: offices as { id: number; name?: string }[],
      }),
    [
      existingSession,
      existingInterview,
      interviewPlan,
      planInterviewers,
      offices,
    ]
  );

  const candidateCurrentTimeLine = useMemo(
    () =>
      buildCandidateCurrentTimeLine(hasCandidateTimezone, candidateCurrentTime),
    [hasCandidateTimezone, candidateCurrentTime]
  );

  const customSchedulePastError =
    timing === 'custom' &&
    Boolean(
      customSchedule?.date &&
      customSchedule?.time &&
      customSchedule?.timezoneKey
    ) &&
    !isCustomScheduleInFuture(customSchedule);

  const scheduledAtISO = useMemo(() => {
    void minuteTick;
    return computeScheduledAtISO({
      timing,
      customSchedule,
      candidateTimezoneKey,
    });
  }, [timing, customSchedule, candidateTimezoneKey, minuteTick]);

  /** Same instant as payload, formatted with account prefs in candidate (or anchor) TZ; abbr matches moment (HST/HDT). */
  const scheduleDisplayInCandidateTimezone = useMemo(() => {
    if (timing === 'immediately') return null;
    if (!scheduledAtISO) return null;
    const m = moment.utc(scheduledAtISO).tz(candidateTimezoneKey);
    return `${m.format(momentDateTimeFormat)} ${m.format('z')}`;
  }, [timing, scheduledAtISO, candidateTimezoneKey, momentDateTimeFormat]);

  // --- Context templates (job config + published) ---
  const { data: jobConfig } = useCoordinatorJobConfig(
    jobId !== null ? String(jobId) : null,
    true
  );
  const { data: publishedTemplates = [] } =
    useCoordinatorTemplates('published');

  const jobTemplates = useMemo(
    () => jobConfig?.coordinator_templates ?? [],
    [jobConfig?.coordinator_templates]
  );

  const templates = useMemo(() => {
    const byId = new Map();
    publishedTemplates.forEach((t) => byId.set(t.id, t));
    jobTemplates.forEach((t) => byId.set(t.id, t));
    return Array.from(byId.values());
  }, [publishedTemplates, jobTemplates]);

  useEffect(() => {
    const sessionHasTemplates =
      existingSession &&
      Array.isArray(existingSession.coordinator_templates) &&
      existingSession.coordinator_templates.length > 0;
    if (sessionHasTemplates) return;
    if (!Array.isArray(jobTemplates) || jobTemplates.length === 0) return;
    const id = setTimeout(() => {
      setSelectedTemplateIds((prev) =>
        prev.length === 0 ? jobTemplates.map((t) => t.id) : prev
      );
    }, 0);
    return () => clearTimeout(id);
  }, [jobTemplates, existingSession, hasExistingInterview]);

  const templateOptions = useMemo(
    () => templates?.map((t) => ({ label: t?.name, value: t?.id })) ?? [],
    [templates]
  );
  const selectedValues = useMemo(
    () => new Set(selectedTemplateIds),
    [selectedTemplateIds]
  );
  const getAvailableOptions = useCallback(
    (currentValue) =>
      templateOptions.filter(
        (opt) => !selectedValues.has(opt.value) || opt.value === currentValue
      ),
    [templateOptions, selectedValues]
  );
  const allTemplatesSelected =
    templateOptions.length > 0 &&
    selectedTemplateIds.length >= templateOptions.length;
  const hasUnselectedRow = selectedTemplateIds.some((id) => !id);
  const addDisabled =
    templateOptions.length === 0 || allTemplatesSelected || hasUnselectedRow;
  const addTooltip =
    templateOptions.length === 0
      ? 'No templates available'
      : allTemplatesSelected
        ? 'All templates selected'
        : hasUnselectedRow
          ? 'Select a template first'
          : '';

  useEffect(() => {
    const len = selectedTemplateIds.length;
    const lastIsEmpty = len > 0 && selectedTemplateIds[len - 1] === '';
    if (len > prevTemplateCountRef.current && lastIsEmpty) {
      const id = setTimeout(() => {
        const input =
          newTemplateRowRef.current?.querySelector?.('input') ||
          newTemplateRowRef.current?.querySelector?.('[class*="control"]');
        if (input?.focus) input.focus();
      }, 50);
      prevTemplateCountRef.current = len;
      return () => clearTimeout(id);
    }
    prevTemplateCountRef.current = len;
  }, [selectedTemplateIds]);

  // --- Submit payload (ref-free slice for render + merge ref only on submit) ---
  const schedulePayloadBase = useMemo(() => {
    const plan = interviewPlan || {};
    const useEditableValues = showInterviewDetailsAsEditable;
    const planLocation =
      plan.location === 'unspecified' || !plan.location ? '' : plan.location;
    const resolvedLocation = useEditableValues ? location : planLocation;
    const planOfficeId =
      (plan as { ats_office_id?: number | null })?.ats_office_id ?? null;
    const resolvedOfficeId = useEditableValues
      ? officeId
      : officeId !== null
        ? officeId
        : planOfficeId;
    const resolvedDuration = useEditableValues
      ? durationMinutes
      : plan.duration || 60;
    const participantIds =
      useEditableValues && selectedInterviewers.length > 0
        ? selectedInterviewers.map((opt) => opt.value)
        : planInterviewers.map((p) => p.id);
    const kind = useEditableValues
      ? interviewKind
      : plan.kind || plan.interview_kind || 'one_on_one';

    return {
      interview_name:
        interviewName?.trim() ||
        plan.substituted_name ||
        plan.name ||
        activity?.label ||
        '',
      interview_kind: kind,
      duration_minutes: resolvedDuration,
      location: resolvedLocation,
      ...(resolvedLocation === 'onsite' &&
        resolvedOfficeId !== null && { ats_office_id: resolvedOfficeId }),
      participant_ids: participantIds,
      note_taker_enabled: noteTakerEnabled,
      calendar_visibility: calendarVisibility,
      scheduled_at: timing === 'immediately' ? null : scheduledAtISO,
      coordinator_template_id: selectedTemplateIds[0] || undefined,
      ...((existingSession || hasExistingInterview) && {
        reschedule_reason: rescheduleReason?.trim() || '',
      }),
    };
  }, [
    interviewPlan,
    interviewName,
    showInterviewDetailsAsEditable,
    selectedInterviewers,
    planInterviewers,
    interviewKind,
    durationMinutes,
    location,
    officeId,
    activity,
    scheduledAtISO,
    timing,
    selectedTemplateIds,
    noteTakerEnabled,
    calendarVisibility,
    existingSession,
    hasExistingInterview,
    rescheduleReason,
  ]);

  const resolvePayloadValues = useCallback(() => {
    return {
      ...schedulePayloadBase,
      additional_context:
        additionalContextFormikRef.current?.values?.additional_context?.trim?.() ||
        additionalContextFormikRef.current?.values?.additional_context ||
        '',
    };
  }, [schedulePayloadBase]);

  const handleSubmit = useCallback(() => {
    if (isPlanLoading) {
      toast({
        title: COPY.submitValidationPlanLoading,
        status: 'error',
      });
      return;
    }

    const hasCustomDateSelectedNow =
      timing !== 'custom' ||
      (Boolean(customSchedule?.date) &&
        Boolean(customSchedule?.time) &&
        Boolean(customSchedule?.timezoneKey));
    if (!hasCustomDateSelectedNow) {
      toast({
        title: COPY.submitValidationDateTime,
        status: 'error',
      });
      return;
    }

    if (timing === 'custom' && !isCustomScheduleInFuture(customSchedule)) {
      toast({
        title: COPY.customScheduleInPast,
        status: 'error',
      });
      return;
    }

    /** Match payload resolution so we never submit onsite without ats_office_id when the API expects it. */
    const preview = resolvePayloadValues();
    if (!preview.location || preview.location === 'unspecified') {
      toast({
        title: COPY.submitValidationLocation,
        status: 'error',
      });
      return;
    }
    if (onsitePayloadMissingOffice(preview as PayloadLocationOffice)) {
      setIsInterviewDetailsEditing(true);
      toast({
        title: COPY.submitValidationOffice,
        status: 'error',
      });
      window.setTimeout(() => {
        officeAddressFieldRef.current?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
      return;
    }

    const hasInterviewersFromPlanOrSelection =
      selectedInterviewers.length > 0 || planInterviewers.length > 0;
    if (!hasInterviewersFromPlanOrSelection) {
      toast({
        title: COPY.submitValidationInterviewers,
        status: 'error',
      });
      return;
    }
    if (needsInterviewerInput && selectedInterviewers.length === 0) {
      toast({
        title: COPY.submitValidationInterviewers,
        status: 'error',
      });
      return;
    }

    onSubmit(preview);
  }, [
    isPlanLoading,
    timing,
    customSchedule,
    selectedInterviewers.length,
    planInterviewers.length,
    needsInterviewerInput,
    onSubmit,
    resolvePayloadValues,
    toast,
    setIsInterviewDetailsEditing,
  ]);

  const hasCustomDateSelected =
    timing !== 'custom' ||
    (Boolean(customSchedule?.date) &&
      Boolean(customSchedule?.time) &&
      Boolean(customSchedule?.timezoneKey));
  const hasInterviewerWhenRequired =
    !showInterviewDetailsAsEditable || selectedInterviewers.length > 0;

  const interviewPlaceValid = useMemo(() => {
    const p = schedulePayloadBase;
    return (
      Boolean(p.location) &&
      p.location !== 'unspecified' &&
      onsitePayloadHasOfficeWhenRequired(p as PayloadLocationOffice)
    );
  }, [schedulePayloadBase]);

  /**
   * Read-only: gate on location/office + timing + interviewers (matches payload).
   * Interview details editable: keep primary action enabled; handleSubmit still validates (toasts).
   */
  const canSubmit =
    !isSubmitting &&
    !isPlanLoading &&
    (showInterviewDetailsAsEditable ||
      (interviewPlaceValid &&
        hasCustomDateSelected &&
        !customSchedulePastError &&
        hasInterviewerWhenRequired));

  useImperativeHandle(
    ref,
    () => ({
      submit: handleSubmit,
    }),
    [handleSubmit]
  );

  const submitLabel =
    timing === 'immediately' ? COPY.callNow : COPY.scheduleCall;

  // --- Unsaved changes (drawer close confirmation) ---
  const getFormSnapshot = useCallback(
    () =>
      JSON.stringify({
        timing,
        selectedTemplateIds: [...selectedTemplateIds].sort(),
        customSchedule: customSchedule
          ? {
              date: customSchedule.date
                ? moment(customSchedule.date).format('YYYY-MM-DD')
                : null,
              time: customSchedule.time,
              timezoneKey: customSchedule.timezoneKey,
            }
          : null,
        interviewKind,
        selectedInterviewerIds: selectedInterviewers.map((x) => x.value).sort(),
        durationMinutes,
        location,
        officeId,
        noteTakerEnabled,
        calendarVisibility,
        rescheduleReason,
      }),
    [
      timing,
      selectedTemplateIds,
      customSchedule,
      interviewKind,
      selectedInterviewers,
      durationMinutes,
      location,
      officeId,
      noteTakerEnabled,
      calendarVisibility,
      rescheduleReason,
    ]
  );

  const prefillSource = existingSession ?? interviewPlan;
  const sourceKey =
    existingSession?.id ??
    interviewPlan?.id ??
    activity?.id ??
    (interviewPlan ? 'plan' : 'none');

  useEffect(() => {
    if (!prefillSource) return;
    if (lastSourceKeyRef.current !== sourceKey) {
      lastSourceKeyRef.current = sourceKey;
      initialSnapshotRef.current = null;
    }
    const hasSnapshot =
      initialSnapshotRef.current !== null &&
      initialSnapshotRef.current !== undefined;
    if (hasSnapshot) return;
    const id = setTimeout(() => {
      initialSnapshotRef.current = getFormSnapshot();
      setHasUnsavedChangesState(
        initialSnapshotRef.current !== getFormSnapshot()
      );
    }, 120);
    return () => clearTimeout(id);
  }, [prefillSource, sourceKey, getFormSnapshot]);

  useEffect(() => {
    const snapshot = initialSnapshotRef.current;
    if (snapshot === null || snapshot === undefined) return;
    setHasUnsavedChangesState(snapshot !== getFormSnapshot());
  }, [getFormSnapshot]);

  // --- Footer / parent sync (drawer primary button) ---
  useEffect(() => {
    if (onScheduleStateChange) {
      onScheduleStateChange({
        canSubmit,
        isSubmitting,
        submitLabel,
        hasUnsavedChanges,
        timing,
      });
    }
  }, [
    onScheduleStateChange,
    canSubmit,
    isSubmitting,
    submitLabel,
    hasUnsavedChanges,
    timing,
  ]);

  // --- Public API (consumed by ScheduleWithAiFormContent) ---
  return {
    timing,
    setTiming,
    selectedTemplateIds,
    setSelectedTemplateIds,
    previewTemplate,
    setPreviewTemplate,
    customDateModalOpen,
    setCustomDateModalOpen,
    customSchedule,
    setCustomSchedule,
    interviewKind,
    setInterviewKind,
    selectedInterviewers,
    setSelectedInterviewers,
    durationMinutes,
    setDurationMinutes,
    location,
    setLocation,
    officeId,
    setOfficeId,
    offices,
    isOfficeLoading,
    interviewName,
    setInterviewName,
    isInterviewDetailsEditing,
    setIsInterviewDetailsEditing,
    noteTakerEnabled,
    setNoteTakerEnabled,
    calendarVisibility,
    setCalendarVisibility,
    rescheduleReason,
    setRescheduleReason,
    additionalContextFormikRef,
    officeAddressFieldRef,
    newTemplateRowRef,
    // Derived / API
    candidateTimezoneKey,
    hasCandidateTimezone,
    existingSession,
    hasExistingInterview,
    showInterviewDetailsAsEditable,
    displayPlan,
    scheduleDisplayInCandidateTimezone,
    scheduleTimingOptions,
    candidateCurrentTimeLine,
    missingTimezoneMessage: COPY.candidateTimezoneMissing,
    customSchedulePastError,
    templates,
    templateOptions,
    getAvailableOptions,
    addDisabled,
    addTooltip,
    availableLocations,
    candidateCountLabel,
  };
}
